// src/data/teleconsultationData.js
// Clinical content and metadata for Cluster B: /cancer-teleconsultation Hub and Spokes

export const TELECONSULTATION_HUB_DATA = {
  title: 'Online Oncologist Consultation & Video Visit | ByOnco',
  metaTitle: 'Online Oncologist Consultation | Oncology Video Visit | ByOnco',
  description: 'Book a HIPAA-compliant online oncologist consultation with US & international NCI-trained cancer specialists. Video visit in 24-48h. Transparent $199 flat fee.',
  h1: 'Online Oncologist Consultation & Virtual Cancer Visit',
  h1Sub: 'Direct Video Consultation with Board-Certified Medical, Surgical & Radiation Oncologists',
  canonical: 'https://www.getbyonco.com/cancer-teleconsultation',

  reviewer: {
    name: 'Dr. Anand Sharma, MD, DM',
    title: 'Senior Consultant Medical Oncologist',
    credentials: 'Board Certified Medical Oncologist, Member ASCO, ESMO, Telemedicine Practice Guidelines Certified',
    lastReviewed: 'September 24, 2026',
    guidelineRef: 'ASCO Telehealth in Oncology Standards & NCCN v2.2026 Guidelines',
  },

  whenToUse: [
    {
      title: 'Discussing a New Diagnosis',
      description: 'You received a biopsy confirmation and need an unhurried, independent oncologist to explain staging, pathology reports, and initial systemic treatment options.',
      icon: 'Stethoscope',
    },
    {
      title: 'Evaluating Treatment Alternatives',
      description: 'Your local team recommended chemotherapy, immunotherapy, or surgery, and you want to verify if targeted therapies, SBRT, or clinical trials are viable alternatives.',
      icon: 'Activity',
    },
    {
      title: 'Managing Side Effects & Toxicities',
      description: 'Struggling with neuropathy, nausea, fatigue, or immunotherapy-related adverse events (irAEs) and need specialized supportive oncology guidance.',
      icon: 'ShieldAlert',
    },
    {
      title: 'Post-Surgery & Adjuvant Decisions',
      description: 'Reviewing pathology margin status, lymph node involvement, and genomic recurrence scores (e.g. Oncotype DX) to decide whether adjuvant chemotherapy is necessary.',
      icon: 'Scissors',
    },
    {
      title: 'Surveillance & Recurrence Concerns',
      description: 'Elevated tumor markers (PSA, CEA, CA-125) or ambiguous scan findings during follow-up requiring specialist interpretation before invasive procedures.',
      icon: 'FileSearch',
    },
    {
      title: 'Deciding Whether to Travel for Care',
      description: 'Determine whether your specific cancer biology requires travel to a major NCI comprehensive cancer center or if standard-of-care can be safely administered locally.',
      icon: 'Building2',
    },
  ],

  whatCanBeEvaluated: [
    {
      feature: 'Comprehensive Record & Pathology Review',
      telehealthCapability: 'Full Evaluation',
      detail: 'Complete audit of surgical pathology, IHC stains, flow cytometry, and genomic sequencing reports (FoundationOne, Guardant, Tempus).',
    },
    {
      feature: 'Radiology & Imaging Cross-Check',
      telehealthCapability: 'Full Evaluation',
      detail: 'Discussion of CT, MRI, and PET-CT scan findings, response assessment (RECIST criteria), and staging confirmation.',
    },
    {
      feature: 'Treatment Protocol Verification',
      telehealthCapability: 'Full Evaluation',
      detail: 'Review of first-line, second-line, and maintenance regimens against current NCCN, ASCO, and ESMO clinical guidelines.',
    },
    {
      feature: 'Clinical Trial Identification',
      telehealthCapability: 'Full Evaluation',
      detail: 'Identification of phase II/III trials matching specific somatic genomic mutations (EGFR, ALK, KRAS, BRCA, etc.).',
    },
    {
      feature: 'Physical Palpation & Direct Exam',
      telehealthCapability: 'In-Person Required',
      detail: 'Superficial lymph node palpation, breast mass measurement, or pelvic exams require local physical examination.',
    },
    {
      feature: 'Acute Oncologic Emergencies',
      telehealthCapability: 'ER / Inpatient Required',
      detail: 'Suspected spinal cord compression, superior vena cava syndrome, or neutropenic fever (>100.4°F) require immediate emergency room care.',
    },
  ],

  transparentPricing: [
    {
      service: 'ByOnco Virtual Video Consultation',
      price: '$199',
      duration: '30 to 45 minutes',
      includes: [
        'Pre-visit record review by oncology navigator',
        'Direct 1-on-1 HIPAA-compliant video visit with board-certified oncologist',
        'Post-consultation written clinical summary report',
        'Actionable questions to bring to your local treating oncologist',
        'No facility fees, no surprise out-of-network balance bills',
      ],
      popular: true,
      ctaText: 'Book Video Consultation',
      ctaLink: '/teleconsultation',
    },
    {
      service: 'US Academic Medical Center Virtual Second Opinion',
      price: '$800 – $2,500+',
      duration: 'Variable (often asynchronous written only)',
      includes: [
        'Often 2-4 week scheduling delay',
        'State-specific licensure restrictions can limit eligibility',
        'Hospital institutional facility fees often billed separately',
        'Insurance pre-authorization frequently denied for out-of-state visits',
      ],
      popular: false,
      ctaText: 'Standard US Hospital Benchmark',
      traditional: true,
    },
  ],

  howItWorksSteps: [
    {
      step: '1',
      title: 'Book Online & Select Specialty',
      desc: 'Choose your cancer type, select a preferred appointment window, and complete secure intake in under 3 minutes.',
    },
    {
      step: '2',
      title: 'Upload Diagnostic Files',
      desc: 'Submit biopsy reports, bloodwork, and DICOM imaging files through our encrypted HIPAA-compliant patient portal.',
    },
    {
      step: '3',
      title: 'Join Encrypted Video Visit',
      desc: 'Connect with your board-certified oncologist via phone, tablet, or laptop without needing specialized software downloads.',
    },
    {
      step: '4',
      title: 'Receive Written Plan & Debrief',
      desc: 'Access your detailed clinical consultation summary, NCCN guideline citations, and next-step roadmap within 24 hours.',
    },
  ],

  faqs: [
    {
      question: 'Can an online oncologist prescribe chemotherapy or radiation remotely?',
      answer: 'Teleconsultations provide expert independent evaluation, treatment guidance, second opinions, and protocol recommendations. Chemotherapy, immunotherapy infusions, and radiation therapy must be administered locally under the direct physical supervision of an authorized local treating facility. Your ByOnco oncologist delivers a detailed clinical summary that your local team can implement immediately.',
    },
    {
      question: 'How do I prepare for my oncology video consultation?',
      answer: 'Prepare a list of your top 5-7 questions, your current medication list (including supplements), and have a family member or caregiver join the call to take notes. Ensure your biopsy pathology report and latest scan summaries have been uploaded to our secure portal prior to your visit so your oncologist can review them in advance.',
    },
    {
      question: 'Is ByOnco’s video consultation platform HIPAA compliant and secure?',
      answer: 'Yes. All video calls and document transfers are protected by bank-level 256-bit TLS encryption, fully compliant with HIPAA, HITECH, and SOC2 security standards. Your health records are never sold or shared without explicit written consent.',
    },
    {
      question: 'Can my family members join the video call from different locations?',
      answer: 'Yes. Our platform allows multi-party video conferencing. Family members, caregivers, or your primary care physician can join the video consultation simultaneously from different cities or devices via a single secure invitation link.',
    },
    {
      question: 'What is the difference between a teleconsultation and an asynchronous second opinion?',
      answer: 'A teleconsultation is a live, interactive 30-45 minute video appointment where you can speak face-to-face with an oncologist, ask live questions, and discuss concerns in real time ($199). An asynchronous second opinion is an exhaustive written multidisciplinary tumor board review of your full pathology slides and radiology scans ($450-$850) delivered within 12-72 hours.',
    },
  ],
};

export const TELECONSULTATION_SPOKES = [
  {
    slug: 'video-consultation-oncologist',
    path: '/video-consultation-oncologist',
    shortName: 'Video Consultation Guide',
    title: 'Video Consultation with an Oncologist | How It Works, Costs & Preparation',
    metaTitle: 'Video Consultation with an Oncologist | Fast Virtual Cancer Visit | ByOnco',
    description: 'Learn how an oncology video consultation works, what records you need, transparent $199 pricing, and how to get an expert cancer specialist video visit within 24-48 hours.',
    h1: 'Video Consultation with a Board-Certified Oncologist',
    h1Sub: 'Connect Face-to-Face with Specialized Cancer Physicians from Home',
    clinicalSummary: 'A direct video consultation enables cancer patients to consult top subspecialty oncologists without the physical strain, travel expenses, or multi-week delays of traveling to distant cancer centers.',
    reviewer: {
      name: 'Dr. Anand Sharma, MD, DM',
      title: 'Senior Consultant Medical Oncologist',
      credentials: 'Board Certified Medical Oncologist, Member ASCO & ESMO',
      lastReviewed: 'September 24, 2026',
      guidelineRef: 'ASCO Telehealth Quality Guidelines 2026',
    },
    keyTopics: [
      {
        title: 'Zero Travel Fatigue',
        desc: 'Consult premier cancer specialists without airports, hotel stays, or immune exposure while immunocompromised.',
      },
      {
        title: 'Pre-Visit Medical Synthesis',
        desc: 'Our oncology navigators organize your records so your physician spends visit time discussing solutions, not sorting paperwork.',
      },
      {
        title: 'Multi-Participant Access',
        desc: 'Family members across multiple states can participate simultaneously on the same secure encrypted video connection.',
      },
      {
        title: 'Immediate Written Action Plan',
        desc: 'Receive an actionable summary documenting staging, treatment rationale, and specific questions for your local team.',
      },
    ],
    faqs: [
      {
        question: 'How fast can I get a video consultation appointment?',
        answer: 'Appointments are routinely available within 24 to 48 hours of submitting your basic clinical intake and medical records.',
      },
      {
        question: 'What technology do I need for the video visit?',
        answer: 'Any smartphone, tablet, laptop, or desktop computer with a functional camera, microphone, and standard web browser (Chrome, Safari, Edge). No software installation is required.',
      },
      {
        question: 'Can I upload scans on CD before the video consultation?',
        answer: 'Yes. Our clinical care team provides a direct cloud uploader for DICOM imaging files (CT, MRI, PET-CT) so the oncologist can review raw scan images before your call.',
      },
      {
        question: 'What if I have connection issues during the video visit?',
        answer: 'If internet instability occurs, our platform seamlessly fails over to an encrypted direct cellular phone call so your clinical consultation is never interrupted.',
      },
    ],
    relatedSpokes: [
      'what-to-expect-oncology-video-visit',
      'breast',
      'lung',
      'rare-cancer',
    ],
  },
  {
    slug: 'what-to-expect-oncology-video-visit',
    path: '/what-to-expect-oncology-video-visit',
    shortName: 'What to Expect Guide',
    title: 'What to Expect During an Oncology Video Visit | Patient Preparation Guide',
    metaTitle: 'What to Expect During an Oncology Video Visit | Checklist & Tips | ByOnco',
    description: 'Complete patient guide on preparing for an online cancer video visit: records checklist, top questions to ask, caregiver participation, and technical setup.',
    h1: 'What to Expect During an Oncology Video Visit',
    h1Sub: 'A Practical Preparation Guide for Patients, Caregivers & Families',
    clinicalSummary: 'Maximize the value of your 30-45 minute oncology virtual visit by understanding the consultation structure, essential records, and highest-priority clinical questions.',
    reviewer: {
      name: 'Dr. Anand Sharma, MD, DM',
      title: 'Senior Consultant Medical Oncologist',
      credentials: 'Board Certified Medical Oncologist, Member ASCO',
      lastReviewed: 'September 24, 2026',
      guidelineRef: 'ASCO Patient-Centered Telehealth Guidance',
    },
    keyTopics: [
      {
        title: 'The First 5 Minutes: Medical History',
        desc: 'Your oncologist confirms your baseline symptoms, previous surgeries, and current functional performance score (ECOG).',
      },
      {
        title: 'Minutes 5 to 20: Diagnostic Deep-Dive',
        desc: 'Reviewing pathology subtypes, margin clearance, genomic biomarkers, and scan findings on shared screen.',
      },
      {
        title: 'Minutes 20 to 35: Treatment Alternatives',
        desc: 'Discussing surgical vs systemic options, sequencing (neoadjuvant vs adjuvant), side effect profiles, and trial options.',
      },
      {
        title: 'Minutes 35 to 45: Q&A & Action Steps',
        desc: 'Directly answering your prepared questions and outlining specific discussion points for your local oncologist.',
      },
    ],
    faqs: [
      {
        question: 'What questions should I prioritize asking during my video visit?',
        answer: 'Focus on: 1) Is my cancer staging and subtype completely confirmed? 2) Are there biomarker tests (NGS, IHC) missing? 3) What are the goals of treatment (curative vs control)? 4) What clinical trials should I consider? 5) What are the most common severe side effects and how are they prevented?',
      },
      {
        question: 'Should I have a caregiver or family member present?',
        answer: 'Yes, we strongly recommend having a trusted family member or advocate present. Cancer consultations involve complex medical terminology and high emotional stress; having a second person take notes ensures no crucial details are missed.',
      },
      {
        question: 'Can the doctor share their screen to show me my scans?',
        answer: 'Yes. Our oncologists frequently share their screen to display raw axial/coronal CT or MRI slices, explaining exact tumor dimensions, lymph node locations, and response to therapy.',
      },
      {
        question: 'What happens after the video consultation ends?',
        answer: 'Within 24 hours, you receive a written clinical consultation summary in your patient portal, documenting all recommendations, guideline references, and specific questions for your local team.',
      },
    ],
    relatedSpokes: [
      'video-consultation-oncologist',
      'breast',
      'prostate',
      'colorectal',
    ],
  },
  {
    slug: 'breast',
    path: '/cancer-teleconsultation/breast',
    shortName: 'Breast Cancer Video Consult',
    title: 'Breast Cancer Teleconsultation & Video Visit Online | ByOnco',
    metaTitle: 'Breast Cancer Teleconsultation | Online Breast Oncologist | ByOnco',
    description: 'Consult an expert breast medical or surgical oncologist online. Discuss HER2 status, Oncotype DX scores, lumpectomy vs mastectomy, and CDK4/6 inhibitors. $199 flat fee.',
    h1: 'Breast Cancer Online Teleconsultation',
    h1Sub: 'Virtual Video Consultations with NCI-Trained Breast Oncology Specialists',
    clinicalSummary: 'Connect with a subspecialized breast oncologist to discuss lumpectomy vs mastectomy, margin status, genomic recurrence scores (Oncotype DX, MammaPrint), HER2-directed therapies, and endocrine treatment sequencing.',
    reviewer: {
      name: 'Dr. Anand Sharma, MD, DM',
      title: 'Senior Consultant Medical Oncologist',
      credentials: 'Breast Oncology Subspecialist, ASCO Member',
      lastReviewed: 'September 24, 2026',
      guidelineRef: 'NCCN Breast Cancer Guidelines v2.2026',
    },
    keyTopics: [
      {
        title: 'HER2 & Hormone Receptor Re-Evaluation',
        desc: 'Evaluate HER2-low status (IHC 1+ or 2+/FISH negative) for eligibility for antibody-drug conjugates like trastuzumab deruxtecan (Enhertu).',
      },
      {
        title: 'Genomic Assay Interpretation',
        desc: 'Review Oncotype DX Recurrence Score (RS) to determine definitive benefit vs futility of adjuvant chemotherapy in HR+/HER2- early breast cancer.',
      },
      {
        title: 'Surgical Margin & Reconstruction Review',
        desc: 'Discuss clear vs close surgical margins, sentinel lymph node dissection vs axillary clearance, and immediate reconstruction options.',
      },
      {
        title: 'CDK4/6 Inhibitors & Endocrine Therapy',
        desc: 'Evaluate adjuvant abemaciclib (Verzenio) or ribociclib (Kisqali) indications for high-risk early hormone receptor-positive disease.',
      },
    ],
    faqs: [
      {
        question: 'Can a virtual breast oncologist help me decide between lumpectomy and mastectomy?',
        answer: 'Yes. By reviewing your breast MRI, mammogram, core biopsy tumor-to-breast ratio, and genetic risk factors (BRCA1/2, PALB2), a virtual breast specialist can objectively discuss breast conservation safety vs bilateral mastectomy indications.',
      },
      {
        question: 'How do I know if I qualify for immunotherapy in triple-negative breast cancer (TNBC)?',
        answer: 'Our oncologists review tumor size (T1c or greater) and nodal status to evaluate neoadjuvant pembrolizumab (Keytruda) regimens (KEYNOTE-522 protocol) and discuss immune-related side effect monitoring.',
      },
      {
        question: 'Can I get a consultation if my cancer has metastasized?',
        answer: 'Yes. Virtual consultations are invaluable for metastatic breast cancer, helping you explore next-line targeted therapies (PIK3CA inhibitors, AKT inhibitors, T-DXd, sacituzumab govitecan) based on updated ctDNA liquid biopsy or tissue NGS.',
      },
      {
        question: 'How does this connect to ByOnco’s breast second opinion service?',
        answer: 'A teleconsultation is a live video conversation. If your case requires slide-by-slide re-staining by our pathology team, your virtual oncologist can seamlessly refer your case to our formal pathology re-review panel.',
      },
    ],
    relatedSpokes: [
      'video-consultation-oncologist',
      'what-to-expect-oncology-video-visit',
      'rare-cancer',
    ],
  },
  {
    slug: 'lung',
    path: '/cancer-teleconsultation/lung',
    shortName: 'Lung Cancer Video Consult',
    title: 'Lung Cancer Teleconsultation & Video Visit Online | ByOnco',
    metaTitle: 'Lung Cancer Teleconsultation | Online Thoracic Oncologist | ByOnco',
    description: 'Connect with a thoracic medical oncologist online. Review EGFR, ALK, ROS1, KRAS G12C mutations, PD-L1 scores, SBRT vs lobectomy, and adjuvant targeted therapies.',
    h1: 'Lung Cancer Online Teleconsultation',
    h1Sub: 'Virtual Video Consultations with Specialized Thoracic Oncologists',
    clinicalSummary: 'Discuss precision non-small cell (NSCLC) and small cell (SCLC) lung cancer treatment strategies with specialized thoracic oncologists. Review NGS panels, third-generation TKIs, immunotherapy combinations, and consolidative radiation.',
    reviewer: {
      name: 'Dr. Anand Sharma, MD, DM',
      title: 'Senior Consultant Thoracic Medical Oncologist',
      credentials: 'Board Certified Medical Oncologist, Member IASLC & ASCO',
      lastReviewed: 'September 24, 2026',
      guidelineRef: 'NCCN Non-Small Cell Lung Cancer Guidelines v3.2026',
    },
    keyTopics: [
      {
        title: 'NGS Biomarker Panel Verification',
        desc: 'Review broad panel testing for EGFR (exon 19/21/20ins), ALK fusions, ROS1, BRAF V600E, RET, MET exon 14 skipping, and KRAS G12C.',
      },
      {
        title: 'Early-Stage Adjuvant Targeted Therapy',
        desc: 'Evaluate adjuvant osimertinib (Tagrisso) after resection for EGFR-mutated stage IB-IIIA NSCLC or adjuvant atezolizumab/pembrolizumab.',
      },
      {
        title: 'SBRT vs Minimally Invasive Surgery',
        desc: 'Determine candidacy for stereotactic body radiotherapy (SBRT/SABR) vs VATS/robotic lobectomy in borderline surgical candidates.',
      },
      {
        title: 'Oligometastatic Disease Strategy',
        desc: 'Evaluate definitive local consolidative therapy (surgery or ablative radiation) for patients with 1-3 distant metastases responding to systemic therapy.',
      },
    ],
    faqs: [
      {
        question: 'Should I start chemotherapy before my NGS genetic test results arrive?',
        answer: 'In non-squamous NSCLC, starting immunotherapy before ruling out EGFR or ALK mutations can cause severe toxicities if a targeted pill is later introduced, and immunotherapy is significantly less effective in oncogene-addicted tumors. A virtual thoracic oncologist can help you determine whether it is clinically safe to wait 7-10 days for comprehensive NGS.',
      },
      {
        question: 'Can a virtual visit evaluate brain metastases from lung cancer?',
        answer: 'Yes. Thoracic specialists review brain MRI imaging and discuss modern brain-penetrant TKIs (e.g. osimertinib, alectinib, lorlatinib) versus stereotactic radiosurgery (SRS) vs whole-brain radiation avoidance.',
      },
      {
        question: 'What if my biopsy didn’t have enough tissue for genetic testing?',
        answer: 'Our oncologists will evaluate whether a ctDNA liquid biopsy blood test (Guardant360, FoundationOne Liquid CDx) is appropriate to detect actionable driver mutations without needing a repeat invasive needle biopsy.',
      },
      {
        question: 'Is virtual lung cancer teleconsultation available worldwide?',
        answer: 'Yes. Patients across the United States and internationally can schedule video visits to receive independent clinical guidance based on current international guidelines.',
      },
    ],
    relatedSpokes: [
      'video-consultation-oncologist',
      'what-to-expect-oncology-video-visit',
      'rare-cancer',
    ],
  },
  {
    slug: 'prostate',
    path: '/cancer-teleconsultation/prostate',
    shortName: 'Prostate Cancer Video Consult',
    title: 'Prostate Cancer Teleconsultation & Video Visit Online | ByOnco',
    metaTitle: 'Prostate Cancer Teleconsultation | Online Urologic Oncologist | ByOnco',
    description: 'Consult an expert urologic oncologist online. Discuss Gleason score interpretation, PSMA PET-CT scans, active surveillance vs robotic prostatectomy vs radiation.',
    h1: 'Prostate Cancer Online Teleconsultation',
    h1Sub: 'Virtual Video Consultations with NCI-Trained Urologic Oncologists',
    clinicalSummary: 'Navigate critical localized and advanced prostate cancer decisions: active surveillance criteria, robotic radical prostatectomy vs SBRT/brachytherapy, Decipher genomic risk scores, and PSMA-directed theranostics.',
    reviewer: {
      name: 'Dr. Anand Sharma, MD, DM',
      title: 'Senior Consultant Urologic Oncologist',
      credentials: 'Board Certified Medical Oncologist, Member ASCO GU',
      lastReviewed: 'September 24, 2026',
      guidelineRef: 'NCCN Prostate Cancer Guidelines v2.2026',
    },
    keyTopics: [
      {
        title: 'Active Surveillance vs Immediate Treatment',
        desc: 'Review PSA density, multiparametric MRI (PIRADS score), and Gleason Grade Group 1-2 to determine if surgery or radiation can be safely deferred.',
      },
      {
        title: 'Genomic Biomarker Stratification',
        desc: 'Evaluate Decipher or Prolaris genomic scores to quantify metastasis risk and determine optimal androgen deprivation therapy (ADT) duration.',
      },
      {
        title: 'Robotic Surgery vs Radiation Therapy',
        desc: 'Unbiased side-by-side comparison of radical prostatectomy vs external beam radiation/SBRT regarding urinary continence, erectile function, and cure rates.',
      },
      {
        title: 'Advanced & Castration-Resistant Disease',
        desc: 'Discuss modern AR pathway inhibitors (enzalutamide, apalutamide, darolutamide), PARP inhibitors for BRCA1/2, and Pluvicto (Lutetium-177 PSMA).',
      },
    ],
    faqs: [
      {
        question: 'Can an online consultation help me decide between surgery and radiation for prostate cancer?',
        answer: 'Yes. Because surgeons often lean toward surgery and radiation oncologists toward radiation, an independent medical/urologic oncologist consultation provides an objective, unbiased evaluation tailored to your age, comorbidities, urinary symptoms, and tumor risk tier.',
      },
      {
        question: 'What is a PSMA PET scan and should I get one before treatment?',
        answer: 'PSMA (Prostate-Specific Membrane Antigen) PET-CT is the most sensitive modern molecular imaging for detecting micro-metastatic prostate cancer. In unfavorable intermediate, high-risk, or biochemically recurrent cases, our oncologists review whether PSMA PET could alter your radiation field or surgical plan.',
      },
      {
        question: 'What should I do if my PSA starts rising after prostate surgery?',
        answer: 'A virtual urologic oncologist will evaluate your PSA doubling time, pathology margin status, and determine the optimal window for early salvage radiation therapy with or without short-term hormone therapy before distant spread occurs.',
      },
      {
        question: 'Can I discuss genetic testing for hereditary prostate cancer?',
        answer: 'Yes. We review indications for germline testing (BRCA1, BRCA2, ATM, CHEK2, Lynch syndrome) which impacts both your treatment options (PARP inhibitors) and cancer screening for your children.',
      },
    ],
    relatedSpokes: [
      'video-consultation-oncologist',
      'what-to-expect-oncology-video-visit',
      'colorectal',
    ],
  },
  {
    slug: 'colorectal',
    path: '/cancer-teleconsultation/colorectal',
    shortName: 'Colorectal Cancer Video Consult',
    title: 'Colorectal Cancer Teleconsultation & Video Visit Online | ByOnco',
    metaTitle: 'Colorectal Cancer Teleconsultation | Online Gastrointestinal Oncologist | ByOnco',
    description: 'Consult an expert GI medical and surgical oncologist online. Discuss MSI-H immunotherapy, extended RAS/BRAF testing, liver metastasis resectability, and total neoadjuvant therapy.',
    h1: 'Colorectal Cancer Online Teleconsultation',
    h1Sub: 'Virtual Video Consultations with Specialized Gastrointestinal Oncologists',
    clinicalSummary: 'Receive expert virtual guidance on colon and rectal cancer care: Total Neoadjuvant Therapy (TNT) for rectal cancer, organ preservation protocols, KRAS/NRAS/BRAF molecular profiling, and multidisciplinary liver metastasectomy planning.',
    reviewer: {
      name: 'Dr. Anand Sharma, MD, DM',
      title: 'Senior Consultant GI Medical Oncologist',
      credentials: 'Board Certified Medical Oncologist, Member ASCO GI',
      lastReviewed: 'September 24, 2026',
      guidelineRef: 'NCCN Colon and Rectal Cancer Guidelines v1.2026',
    },
    keyTopics: [
      {
        title: 'Total Neoadjuvant Therapy (TNT) for Rectal Cancer',
        desc: 'Review delivery of all chemotherapy and chemoradiotherapy upfront prior to surgery to maximize complete clinical response rates.',
      },
      {
        title: 'Non-Operative "Watch & Wait" Management',
        desc: 'Assess whether complete clinical response (cCR) on restaging MRI and endoscopy justifies omitting permanent colostomy surgery under strict surveillance.',
      },
      {
        title: 'Extended RAS, BRAF & HER2 Profiling',
        desc: 'Confirm wild-type KRAS/NRAS status for anti-EGFR therapy (cetuximab, panitumumab) and evaluate BRAF V600E-targeted combinations (encorafenib).',
      },
      {
        title: 'Oligometastatic Liver & Lung Resection',
        desc: 'Multidisciplinary surgical review of liver-confined metastases for curative-intent hepatic resection, portal vein embolization, or hepatic artery infusion (HAI) pumps.',
      },
    ],
    faqs: [
      {
        question: 'Can rectal surgery and a permanent ostomy bag be avoided?',
        answer: 'In locally advanced rectal cancer, Total Neoadjuvant Therapy (TNT) results in a complete clinical response in up to 30-50% of patients. A virtual GI oncologist can evaluate your restaging pelvic MRI and endoscopies to determine if a non-operative "Watch-and-Wait" protocol is a safe alternative to abdominoperineal resection (APR).',
      },
      {
        question: 'What is MSI-High or MMR deficiency, and how does it change treatment?',
        answer: 'Patients with mismatch repair deficiency (dMMR/MSI-H) have extraordinary response rates to immunotherapy (pembrolizumab, dostarlimab, nivolumab/ipilimumab), often achieving complete durable remission without traditional chemotherapy or surgery.',
      },
      {
        question: 'Can stage IV colorectal cancer with liver metastases still be cured?',
        answer: 'Yes. If metastases are confined to the liver or lungs and are technically resectable, 5-year survival rates reach 40-50% following surgical resection or ablation combined with systemic chemotherapy. Our team reviews liver MRI/CT imaging to evaluate resectability.',
      },
      {
        question: 'What is ctDNA (Signatera) testing for colon cancer?',
        answer: 'Circulating tumor DNA (ctDNA) measures microscopic residual cancer DNA in the bloodstream after colon resection. A positive test indicates high recurrence risk warranting chemotherapy, while a persistently negative test may support de-escalation in select stage II cases.',
      },
    ],
    relatedSpokes: [
      'video-consultation-oncologist',
      'what-to-expect-oncology-video-visit',
      'rare-cancer',
    ],
  },
  {
    slug: 'rare-cancer',
    path: '/cancer-teleconsultation/rare-cancer',
    shortName: 'Rare Cancer Video Consult',
    title: 'Rare Cancer Teleconsultation & Video Visit Online | ByOnco',
    metaTitle: 'Rare Cancer Teleconsultation | Expert Sarcoma & Rare Tumor Oncologist | ByOnco',
    description: 'Consult an expert rare cancer subspecialist online. Connect with sarcoma, neuroendocrine, cholangiocarcinoma, and GIST oncologists. Molecular tumor board trial matching.',
    h1: 'Rare Cancer Online Teleconsultation',
    h1Sub: 'Virtual Video Consultations with NCI-Affiliated Rare Tumor Subspecialists',
    clinicalSummary: 'Access rare tumor subspecialists who treat high-volume cohorts of rare malignancies: soft tissue and bone sarcomas, neuroendocrine tumors (NETs), appendiceal carcinomas, salivary gland cancers, and molecularly driven ultra-rare neoplasms.',
    reviewer: {
      name: 'Dr. Anand Sharma, MD, DM',
      title: 'Senior Consultant & Rare Tumor Specialist',
      credentials: 'Board Certified Medical Oncologist, Member CTOS (Connective Tissue Oncology Society) & ASCO',
      lastReviewed: 'September 24, 2026',
      guidelineRef: 'NCCN Soft Tissue Sarcoma & Neuroendocrine Guidelines v2.2026',
    },
    keyTopics: [
      {
        title: 'Central Pathology & Histological Subtyping',
        desc: 'Confirm exact histological and molecular subclassification (over 70 distinct sarcoma subtypes) via RNA sequencing and FISH fusion panels.',
      },
      {
        title: 'Agnostic Genomic Marker Exploitation',
        desc: 'Screen for targetable rare alterations: NTRK gene fusions (larotrectinib), RET fusions, BRAF V600E, FGFR fusions, and high tumor mutational burden (TMB-H).',
      },
      {
        title: 'PRRT & Somatostatin Receptor Targeting',
        desc: 'Review Ga-68 DOTATATE or Cu-64 DOTATATE PET scans for Lutathera (Lu-177 dotatate) peptide receptor radionuclide therapy in well-differentiated NETs.',
      },
      {
        title: 'National & Global Clinical Trial Matching',
        desc: 'Match patients with nationwide open clinical trials, basket trials, and expanded access compassionate use programs for rare oncologic entities.',
      },
    ],
    faqs: [
      {
        question: 'Why is a second opinion video visit critical for rare cancers?',
        answer: 'Most community oncologists see fewer than 2-3 rare cancer cases (like synovial sarcoma or pheochromocytoma) in their entire career. Diagnostic discordance exceeds 25-30% in rare oncology. Consulting a dedicated high-volume rare tumor subspecialist dramatically improves diagnostic precision and unlocks subtype-specific clinical trials.',
      },
      {
        question: 'How do you identify clinical trials for rare cancers?',
        answer: 'Our molecular oncology navigators cross-reference your somatic DNA/RNA sequencing data with active trials across the NCI National Clinical Trials Network (NCTN), basket trials, and pharmaceutical rare disease registries to identify actionable study protocols.',
      },
      {
        question: 'Can I do a teleconsultation if I live in a rural area or outside the United States?',
        answer: 'Yes. Teleconsultations are designed specifically to bridge geographical divides, allowing patients in underserved rural regions or international countries to consult premier rare disease specialists without requiring immediate travel.',
      },
      {
        question: 'What records are most important for a rare cancer consult?',
        answer: 'The initial surgical biopsy pathology report, original formalin-fixed paraffin-embedded (FFPE) slide review notes, comprehensive genomic profiling (NGS) report, and raw DICOM imaging discs.',
      },
    ],
    relatedSpokes: [
      'video-consultation-oncologist',
      'what-to-expect-oncology-video-visit',
      'breast',
      'lung',
    ],
  },
];
